import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import { AsyncMultiSelectField } from '../../containers/Form';
import { actions } from '../../pages/SuperUser/Patients/Cohorts/redux/actions';
import Strings from '../../Strings';
import { SelectedOptions } from '../../components/Select/Multiselect';
import { renderPrescriber } from '../../pages/SuperUser/Patients/PatientInfo/MedicalHistory';
import TableWithPagination, { Column } from '../../containers/Table/TableWithPagination';
import { DATE_FORMAT_MONTH_NAME } from '../../constants';
import { openModal } from '../../actions/modal';
import { conditionFilterOption, conditionKey, createConditionOptionsLoader } from '../../utils/rpmPatientFields';

export const getLabelTextForCondition = option => {
  return `${option.code ? '* ' : ''}${option.condition_icd10cm || option.code ? `(${option.condition_icd10cm || option.code}) ` : ''}${option.label}`
};

export const renderInsurance = insurance => {
  if (!insurance) {
    return '-';
  }
  const name = insurance.insuranceName || '';
  const id = insurance.policyId ? `ID: ${insurance.policyId}` : '';
  const grpNo = insurance.groupNumber ? `Grp no.: ${insurance.groupNumber}` : '';
  const bracket = [id, grpNo].filter(Boolean).join(' ');
  if (!name && !bracket) {
    return '-';
  }
  if (!name) {
    return bracket;
  }
  return `${name}${bracket ? ` (${bracket})` : ''}`;
};

export const renderInsuranceInfo = insurance => {
  if (!insurance) {
    return '-';
  }
  return insurance.map(i => renderInsurance(i)).join(', ');
};

export const getLabelForCondition = (option, patient) => (
  <React.Fragment key={`option_${option.value}_${patient.conditionsObjects?.some(c => c.value === option.value)}`}>
    {getLabelTextForCondition(option)}
    <input type="checkbox" defaultChecked={patient.conditionsObjects?.some(c => c.value === option.value)} disabled={option.isDisabled} />
  </React.Fragment>
);

const addMissingConditions = (conditionsObjects, conditionsToAdd) => {
  const missing = (conditionsToAdd || []).filter(c => !(conditionsObjects || []).some(co => conditionKey(co) === conditionKey(c)));
  return missing.length > 0 ? [...(conditionsObjects || []), ...missing] : conditionsObjects;
};

function MedicalHistoryPage(props) {
  const { patient, highlightInvalidFields, validationErrors, fields, setPatient, openEditMedicationModal, openConfirmModal } = props;
  const { conditions: orgConditions, getIdcConditions } = props;

  const loadOptionsConditions = createConditionOptionsLoader(getIdcConditions, orgConditions);

  const openMedicationModal = modalData => openEditMedicationModal({
    ...modalData,
    patient,
    orgConditions,
    loadConditionOptions: createConditionOptionsLoader(getIdcConditions, orgConditions),
  });

  const medicalHistoryPage = (
    <>
      {props.conditions && (
        <AsyncMultiSelectField
          name="conditions"
          id="conditions"
          label={Strings.condition}
          placeholder={Strings.selectCondition}
          value={patient.conditionsObjects || []}
          defaultOptions={props.conditions?.map(c => ({ ...c, value: c.id, label: c.title, isDisabled: !!c.isRemovalBlockedByEnrollment })).concat(patient.conditionsObjects?.filter(o => o.code) || [])}
          loadOptions={loadOptionsConditions}
          onChange={options => setPatient(p => ({ ...p, conditionsObjects: options }))}
          isRequired={fields.conditions.required}
          disabled={fields.conditions.readOnly}
          highlightInvalid={highlightInvalidFields}
          errorsForTooltip={validationErrors.find(v => v.property === 'conditions')?.errors || []}
          checkboxFirst
          rowClassName="double-width-left"
          isSearchable
          filterOption={conditionFilterOption}
          getLabel={option => getLabelForCondition(option, patient)}
        />
      )}
      {patient.conditionsObjects?.length > 0 && props.conditions ? (
        <SelectedOptions
          className="grid-full-width"
          items={patient.conditionsObjects?.map(c => {
            const isLocked = !!c.isRemovalBlockedByEnrollment;
            return {
              ...c,
              label: getLabelTextForCondition(c),
              disabled: isLocked,
              tooltip: isLocked ? Strings.capPatient.conditionAssignedToActiveEnrollment : undefined,
            };
          }) || []}
          onRemove={e => setPatient(p => ({ ...p, conditionsObjects: p.conditionsObjects.filter(h => h.value !== e.value) }))}
          itemsPerRow={2}
        />
      ) : (
        <div>{Strings.noConditionsSelected}</div>
      )}
      <div className="grid-full-width">
        <div style={{color: "#4f4f4f"}}>{Strings.medications}</div>
        <TableWithPagination
          name="patient-medications"
          data={patient.medications?.map(m => ({
            ...m,
            conditionTitles: m.conditions
              ?.map(key => patient.conditionsObjects?.find(co => conditionKey(co) === key)?.title)
              .filter(Boolean),
          })) || []}
          buttons={[
            {
              icon: 'edit',
              onClick: id => {
                const medIndex = parseInt(id, 10);
                openMedicationModal({
                  medication: patient.medications[medIndex],
                  medIndex,
                  onConfirm: (updatedMedication, i, medicationConditions) => {
                    setPatient(p => ({
                      ...p,
                      conditionsObjects: addMissingConditions(p.conditionsObjects, medicationConditions),
                      medications: p.medications.map((m, index) => index === i ? { ...updatedMedication } : m),
                    }));
                  }
                });
              },
            },
            {
              icon: 'delete',
              text: Strings.delete,
              onClick: (id, medication) => {
                const medIndex = parseInt(id, 10);
                openConfirmModal({
                  title: (
                    <span>
                      {Strings.deleteMedicationWarning} <b>{medication?.name || '-'}</b>?
                    </span>
                  ),
                  hideCaution: true,
                  onConfirmAction: () => {
                    setPatient(p => ({
                      ...p,
                      medications: (p.medications || []).filter((_, index) => index !== medIndex),
                    }));
                    return Promise.resolve();
                  },
                });
              },
            },
          ]}
        >
          <Column key="name" title={Strings.medication} value={e => e.name || '-'} />
          <Column key="ndc" title="NDC" value={e => e.ndc || '-'} />
          <Column key="sig" title={Strings.patient_medications.sig} value={e => e.sig || '-'} />
          <Column key="prescriber" title={Strings.prescriber} value={e => e.prescriber ? renderPrescriber(e.prescriber) : '-'} />
          <Column key="fillDate" title={Strings.patient_medications.fillDate} value={e => e.fillDate ? moment(e.fillDate).format(DATE_FORMAT_MONTH_NAME) : '-'}/>
          <Column key="rxNumber" title={Strings.patient_medications.rxNumber} value={e => e.rxNumber || '-'} />
          <Column key="insurance" title={Strings.capPatient.insurance} value={e => renderInsuranceInfo(e.insurance)} />
          <Column key="condition" title={Strings.condition} value={e => e.conditionTitles?.join(', ') || '-'} />
        </TableWithPagination>
      </div>
      <button
        type="button"
        className="link-button"
        onClick={() => {
          openMedicationModal({
            onConfirm: (medication, _medIndex, medicationConditions) => {
              setPatient(p => ({
                ...p,
                conditionsObjects: addMissingConditions(p.conditionsObjects, medicationConditions),
                medications: [...(p.medications || []), { ...medication, edited: false }],
              }));
            }
          });
        }}
      >
        {Strings.addMedication}
      </button>
    </>
  );

  return medicalHistoryPage;
}

const mapDispatchToProps = dispatch => ({
  getIdcConditions: pageRequest => dispatch(actions.getIdcConditions(pageRequest)),
  openEditMedicationModal: data => dispatch(openModal('edit-medication', data)),
  openConfirmModal: data => dispatch(openModal('confirmation-modal', data)),
});

export default connect(null, mapDispatchToProps)(MedicalHistoryPage);
